import React, { useState } from 'react';

/**
 * DirectionsList — renders Valhalla turn-by-turn maneuvers.
 *
 * Props:
 *   maneuvers: [{ instruction, distance_km, time_min, type, street }]
 *   locale:    locale object (for distance formatting); optional
 *
 * Collapsed by default to a summary row — long routes can have 50+ steps,
 * and the result page already carries a lot. Tap to expand the full list.
 */

// Valhalla maneuver type → glyph. Codes from Valhalla's TripLeg maneuver
// type enum. We don't have an icon for every code; unknown codes fall back
// to a neutral dot. Kept as simple unicode so there's no icon dependency.
const TYPE_GLYPH = {
  0: '•',    // kNone
  1: '▲',    // kStart
  2: '▲',    // kStartRight
  3: '▲',    // kStartLeft
  4: '◆',    // kDestination
  5: '◆',    // kDestinationRight
  6: '◆',    // kDestinationLeft
  7: '↑',    // kBecomes
  8: '↑',    // kContinue
  9: '↗',    // kSlightRight
  10: '→',   // kRight
  11: '↘',   // kSharpRight
  12: '↩',   // kUturnRight
  13: '↩',   // kUturnLeft
  14: '↙',   // kSharpLeft
  15: '←',   // kLeft
  16: '↖',   // kSlightLeft
  17: '↑',   // kRampStraight
  18: '↗',   // kRampRight
  19: '↗',   // kRampRight
  20: '↖',   // kRampLeft
  21: '↗',   // kExitRight
  22: '↖',   // kExitLeft
  23: '↑',   // kStayStraight
  24: '↗',   // kStayRight
  25: '↖',   // kStayLeft
  26: '↻',   // kMerge / roundabout-ish
  27: '↻',   // kRoundaboutEnter
  28: '↻',   // kRoundaboutExit
};

export function glyphFor(type) {
  return TYPE_GLYPH[type] || '•';
}

function formatDistance(km, locale) {
  if (km == null) return '';
  // Below 1 km, show meters
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
}

export default function DirectionsList({ maneuvers, locale, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!Array.isArray(maneuvers) || maneuvers.length === 0) {
    return null;
  }

  const stepCount = maneuvers.length;

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '0.5px solid var(--border-default)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 8,
      }}
    >
      {/* Header — always visible, toggles expansion */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          Turn-by-turn directions
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {stepCount} steps {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Step list — only when expanded */}
      {expanded && (
        <div
          style={{
            maxHeight: 360,
            overflowY: 'auto',
            borderTop: '0.5px solid var(--border-subtle)',
          }}
        >
          {maneuvers.map((m, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 14px',
                borderBottom: i < maneuvers.length - 1
                  ? '0.5px solid var(--border-subtle)'
                  : 'none',
              }}
            >
              {/* Step number + glyph */}
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 24,
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    color: 'var(--accent-primary)',
                    lineHeight: 1,
                  }}
                >
                  {glyphFor(m.type)}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    color: 'var(--text-tertiary)',
                    marginTop: 3,
                  }}
                >
                  {i + 1}
                </span>
              </div>

              {/* Instruction + distance */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-primary)',
                    lineHeight: 1.4,
                  }}
                >
                  {m.instruction}
                </div>
                {m.distance_km > 0 && (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      marginTop: 2,
                    }}
                  >
                    {formatDistance(m.distance_km, locale)}
                    {m.time_min > 0 && ` · ${Math.round(m.time_min)} min`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
